// The Schedule board's filters, in the same lozenge vocabulary the availability
// calendar uses for its trainers.
//
// Two axes, because a barn asks two different questions of a week: "what is
// Kate doing" and "when does anything get fed". They compose — Grooms × Feed is
// the morning feed rota — and both live in the URL, so a filtered week is a
// link somebody can send.
//
// Three rows, three colors. The rows stack, and with one hue across all of them
// a glance couldn't tell where the roles ended and the activities began — the
// person chips in particular sat in a run of identical pills that happened to
// mean something else. Color is the cheapest way to say "this is a different
// question", and it's carried by the row label and the chip edges as well as by
// the filled state, so a row reads as its color even when nothing in it is on.
//
// The hues are identity, not status: this is a filter bar, and nothing in it is
// late or done. It stays clear of amber and rose for that reason — those mean
// something specific everywhere else in the app.
//
// Every lozenge carries its count. That is not decoration: it means the row
// tells you the shape of the week BEFORE you click anything, and a filter that
// would empty the board is visible as a zero rather than discovered by trying
// it. It's the same reason the availability chips carry booked/total.
//
// The who axis is two-tier. A role on its own is a real filter — "show me the
// grooms" — and picking one reveals the people in that role who actually have
// something this week. Listing every member regardless would be a wall of
// chips at a barn with forty riders, and a chip that filters to nothing is
// worse than no chip.

// Spelled out in full, because Tailwind builds its stylesheet by scanning
// source for literal class strings — `bg-${hue}-600` compiles to nothing.
const TONES = {
  roles: {
    label: 'text-hunter',
    on: 'bg-hunter text-white border-hunter',
    off: 'bg-white text-ink-700 border-hunter/30 hover:border-hunter',
    count: 'text-hunter/70',
    ring: 'border-hunter/50',
  },
  people: {
    label: 'text-violet-700',
    on: 'bg-violet-600 text-white border-violet-600',
    off: 'bg-white text-ink-700 border-violet-300 hover:border-violet-500',
    count: 'text-violet-500',
    ring: 'border-violet-400',
  },
  // Sky rather than indigo: indigo sat a few degrees from the violet above it
  // and the two rows read as one long purple run. Green, violet, blue are far
  // enough apart to be told apart at a glance, which is the whole point.
  what: {
    label: 'text-sky-700',
    on: 'bg-sky-600 text-white border-sky-600',
    off: 'bg-white text-ink-700 border-sky-300 hover:border-sky-500',
    count: 'text-sky-600',
    ring: 'border-sky-400',
  },
};

const CHIP =
  'inline-flex items-center gap-1.5 text-xs px-2.5 py-1 rounded-full border transition-colors';

function Chip({ tone, active, onClick, children, count, dot }) {
  const t = TONES[tone];
  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={active}
      className={`${CHIP} ${active ? t.on : t.off}`}
    >
      {dot !== undefined &&
        (active ? (
          <span className="w-2 h-2 rounded-full bg-white/80 shrink-0" />
        ) : (
          <span className={`w-2 h-2 rounded-full border shrink-0 ${t.ring}`} />
        ))}
      <span>{children}</span>
      {count !== undefined && (
        <span className={'tabular-nums ' + (active ? 'text-white/70' : t.count)}>{count}</span>
      )}
    </button>
  );
}

/** The quiet reset at the end of a row — a control, so it stays neutral. */
function ClearChip({ onClick, children }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`${CHIP} bg-white text-ink-500 border-dashed border-ink-300 hover:border-ink-500 ml-1`}
    >
      {children}
    </button>
  );
}

function Row({ label, tone, children }) {
  return (
    <div className="flex items-start gap-2">
      <span
        className={
          'text-[10px] font-semibold uppercase tracking-wider pt-2 w-10 shrink-0 ' +
          (tone ? TONES[tone].label : 'text-ink-400')
        }
      >
        {label}
      </span>
      <div className="flex flex-wrap items-center gap-1.5 min-w-0">{children}</div>
    </div>
  );
}

export default function BoardFilters({
  roles,        // [{ id, label, count }] — only roles with something this week
  role,         // selected role id, or null
  onRole,
  members,      // [{ ref, name, count }] within the selected role
  who,          // selected person ref, or null
  onWho,
  activities,   // [{ key, label, count }]
  activity,     // selected activity key, or null
  onActivity,
  showing,      // items after filtering
  total,        // items before filtering
}) {
  const filtered = role || who || activity;
  // The label on the person row names the role it belongs to, so the second
  // tier says whose people these are rather than relying on the indent alone.
  const roleLabel = roles.find((r) => r.id === role)?.label ?? '';

  return (
    <div className="flex flex-col gap-2">
      {roles.length > 0 && (
        <Row label="Who" tone="roles">
          {roles.map((r) => (
            <Chip
              key={r.id}
              tone="roles"
              dot
              active={role === r.id}
              count={r.count}
              onClick={() => onRole(r.id)}
            >
              {r.label}
            </Chip>
          ))}
          {(role || who) && <ClearChip onClick={() => onRole(null)}>Everyone</ClearChip>}
        </Row>
      )}

      {/* The second tier only exists once a role is picked, and only lists the
          people in it with something on this week. */}
      {role && members.length > 0 && (
        <Row label={roleLabel} tone="people">
          {members.map((m) => (
            <Chip
              key={m.ref}
              tone="people"
              active={who === m.ref}
              count={m.count}
              onClick={() => onWho(who === m.ref ? null : m.ref)}
            >
              {m.name}
            </Chip>
          ))}
        </Row>
      )}

      {activities.length > 0 && (
        <Row label="What" tone="what">
          {activities.map((a) => (
            <Chip
              key={a.key}
              tone="what"
              active={activity === a.key}
              count={a.count}
              onClick={() => onActivity(activity === a.key ? null : a.key)}
            >
              {a.label}
            </Chip>
          ))}
          {activity && <ClearChip onClick={() => onActivity(null)}>Anything</ClearChip>}
        </Row>
      )}

      {/* Only once something is hidden. A count of what you're not seeing is
          the difference between a filtered board and a board that looks empty. */}
      {filtered && (
        <p className="text-xs text-ink-400 pl-12">
          Showing <span className="tabular-nums">{showing}</span> of{' '}
          <span className="tabular-nums">{total}</span>
        </p>
      )}
    </div>
  );
}
